import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { map } from 'lit/directives/map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { breakpoints } from '@cloudsuite/mosaic-shared/styles/breakpoints.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import type { PdpBundleProduct, PdpProduct, PdpProductBundle } from 'components/discovery/pdp/typing/pdp-product.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'
import { style } from './discount-bundle.style.js'

export const discountBundleHiddenFields = ['title', 'badge', 'description'] as const

export class DiscountBundleAddedEvent extends Event {
  static type = 'discount-bundle-add' as const

  constructor(public readonly bundleId: number, public readonly productId: number) {
    super(DiscountBundleAddedEvent.type, { bubbles: true, composed: true })
  }
}

type PaperVariant = 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
type HeadingVariant = 'heading-1' | 'heading-2' | 'heading-3' | 'heading-4' | 'heading-5' | 'heading-6'
type BadgeVariant = 'default' | 'error' | 'info' | 'success' | 'warning' | 'new' | 'our-choice' | 'bestseller' | 'sale' | 'premium'
type DiscountBundleLayout = 'default' | 'side' | 'top'

const headingTagByVariant: Record<HeadingVariant, 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'> = {
  'heading-1': 'h1',
  'heading-2': 'h2',
  'heading-3': 'h3',
  'heading-4': 'h4',
  'heading-5': 'h5',
  'heading-6': 'h6',
}

@customElement('csc-discount-bundle')
export class CSCDiscountBundle extends HiddenFieldsMixin(LitElement, discountBundleHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @inject(PDP_PRODUCT_STORE)
  private store!: PdpProductStore

  @property({ type: String, attribute: 'paper-variant' })
  paperVariant: PaperVariant = 'secondary'

  @property({ type: String, attribute: 'heading-variant' })
  headingVariant: HeadingVariant = 'heading-4'

  @property({ type: String, attribute: 'badge-variant' })
  badgeVariant: BadgeVariant = 'sale'

  @property({ type: String })
  layout: DiscountBundleLayout = 'default'

  @state()
  private bundles: PdpProductBundle[] = []

  @state()
  private isMobile = false

  private product: PdpProduct | null = null
  private mediaQuery: MediaQueryList | undefined

  private onMediaChange = (event: MediaQueryListEvent) => {
    this.isMobile = event.matches
  }

  constructor() {
    super()
    createEffect(() => {
      const product = this.store.product()
      if (!product) return
      this.product = product
      this.bundles = product.discount_bundles?.bundles ?? []
    })
  }

  override connectedCallback(): void {
    super.connectedCallback()
    this.mediaQuery = window.matchMedia(`(max-width: ${breakpoints.small - 1}px)`)
    this.isMobile = this.mediaQuery.matches
    this.mediaQuery.addEventListener('change', this.onMediaChange)
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.mediaQuery?.removeEventListener('change', this.onMediaChange)
  }

  private handleAdd(bundle: PdpProductBundle) {
    const productId = this.product?.id ?? bundle.products[0]?.id
    if (productId == null) return
    this.dispatchEvent(new DiscountBundleAddedEvent(bundle.id, productId))
  }

  private renderProductPicture = (product: PdpBundleProduct, clickable = false) => html`
    <cse-selectable class="csc-discount-bundle__product-picture" size="extraSmall">
      <csc-product-image
        source="${product.picture.small}"
        alt="${product.name}"
        width="64"
        height="64"
        fit="contain"
        url="${ifDefined(clickable ? product.page_link : undefined)}"
      ></csc-product-image>
    </cse-selectable>
  `

  private renderProducts(bundle: PdpProductBundle, extraClass = '') {
    const [mainProduct, ...otherProducts] = bundle.products

    if (!mainProduct) return nothing

    return html`
      <cse-group class="${extraClass}" direction="row" alignment="center" column-gap="05" row-gap="05">
        ${this.renderProductPicture(mainProduct)}
        ${otherProducts.length > 0 ? html`
          <cse-icon class="csc-discount-bundle__connector" icon-name="circle-plus" icon-style="solid" size="xl"></cse-icon>
          ${map(otherProducts, product => this.renderProductPicture(product, true))}
        ` : nothing}
      </cse-group>
    `
  }

  private renderMobileProductPicture = (product: PdpBundleProduct, clickable = false) => html`
    <cse-selectable class="csc-discount-bundle__mobile-product-picture">
      <cse-group direction="row" alignment="center" column-gap="05">
        <csc-product-image
          class="csc-discount-bundle__mobile-product-picture-image"
          source="${product.picture.small}"
          alt="${product.name}"
          width="64"
          height="64"
          fit="contain"
          url="${ifDefined(clickable ? product.page_link : undefined)}"
        ></csc-product-image>
        <cse-text as="h6" variant="heading-6">${product.name}</cse-text>
      </cse-group>
    </cse-selectable>
  `

  private renderMobileProducts(bundle: PdpProductBundle) {
    const [mainProduct, ...otherProducts] = bundle.products

    if (!mainProduct) return nothing

    return html`
      <cse-group direction="column" row-gap="05">
        ${this.renderMobileProductPicture(mainProduct)}
        ${otherProducts.length > 0 ? html`
          <cse-text class="csc-discount-bundle__included-accessories" variant="heading-6">
            ${t('included_accessories', { ns: 'csc-discount-bundle', default: 'Included accessories' })}
          </cse-text>
          ${map(otherProducts, product => this.renderMobileProductPicture(product, true))}
        ` : nothing}
      </cse-group>
    `
  }

  private renderHeader(bundle: PdpProductBundle) {
    return html`
      <cse-group direction="column">
        <cse-group direction="row" justify="space-between">
          ${!this.isHidden('title') ? html`
            <cse-text as="${headingTagByVariant[this.headingVariant]}" variant="${this.headingVariant}">${bundle.name}</cse-text>
          ` : nothing}
          ${!this.isHidden('badge') ? html`
            <cse-badge variant="${this.badgeVariant}" composition="solid">
              ${t('discount_badge', { ns: 'csc-discount-bundle', default: '{{variable_discount}} discount', variable_discount: bundle.discount })}
            </cse-badge>
          ` : nothing}
        </cse-group>
        ${!this.isHidden('description') && bundle.description ? html`
          <cse-text variant="body">${bundle.description}</cse-text>
        ` : nothing}
      </cse-group>
    `
  }

  private renderPriceAndCta(bundle: PdpProductBundle) {
    return html`
      <cse-group
        class="csc-discount-bundle__price-cta"
        direction="row"
        justify="flex-end"
        column-gap="06"
        alignment="flex-end"
      >
        <csc-price
          sale-price-formatted="${bundle.price_discount}"
          lead="${bundle.price_normal}"
          display-as-discount
        ></csc-price>
        <csc-order-form-button
          show-label
          @order-form-add="${() => this.handleAdd(bundle)}"
        ></csc-order-form-button>
      </cse-group>
    `
  }

  private renderDesktopLayout(bundle: PdpProductBundle) {
    if (this.layout === 'top') {
      return html`${this.renderPriceAndCta(bundle)}${this.renderProducts(bundle)}`
    }

    if (this.layout === 'side') {
      return html`
        <cse-group nowrap direction="row" justify="space-between" alignment="center" column-gap="06">
          ${this.renderProducts(bundle, 'csc-discount-bundle__products--flexible')}
          ${this.renderPriceAndCta(bundle)}
        </cse-group>
      `
    }

    return html`${this.renderProducts(bundle)}${this.renderPriceAndCta(bundle)}`
  }

  private renderMobileLayout(bundle: PdpProductBundle) {
    if (this.layout === 'top') {
      return html`${this.renderPriceAndCta(bundle)}${this.renderMobileProducts(bundle)}`
    }

    return html`${this.renderMobileProducts(bundle)}${this.renderPriceAndCta(bundle)}`
  }

  private renderBundle = (bundle: PdpProductBundle) => html`
    <cse-paper class="csc-discount-bundle" variant="${this.paperVariant}">
      ${this.renderHeader(bundle)}
      ${this.isMobile ? this.renderMobileLayout(bundle) : this.renderDesktopLayout(bundle)}
    </cse-paper>
  `

  override render() {
    if (this.bundles.length <= 0) return nothing

    return html`
      <cse-group direction="column" row-gap="08">
        ${map(this.bundles, this.renderBundle)}
      </cse-group>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-discount-bundle': CSCDiscountBundle
  }
}
